<script>
//
// A flexible component for handling selections of arbitrary objects.
//
//  v-model - Where to bind the selection. This will be an array containing the selected items.
//  items* (array) - An array of objects. Each object must have at least one unique property called
//    'id' (you can use key-prop to customize this).
//  multiple* (boolean) - Whether or not multiple items can be selected.
//  can-select (function) - A function to be called before an item is selected. The function should
//    return true if the item can be selected, or false to skip selection.
//  key-prop (string) - The name of each items' unique property, default 'id' (e.g. item.id).
//
// Usage:
//
// <selectable
//   ref="selectable"
//   v-model="selection"
//   :items="items"
//   :multiple="true"
//   :can-select="item => !item.disabled"
//   key-prop="id"
//   @input="handleSelection"
// >
//   <div
//     v-for="item in items"
//     :key="item.id"
//     :class="{ selected: $refs.selectable.isSelected(item) }"
//     @click="$refs.selectable.select(item, $event)
//   />
// </selectable>
//
// Provide a ref to the selectable component and access methods using:
//
//  $refs.selectable.<method>()
//
// Methods:
//
//  isSelected(item) - Tells if the specified item is selected. Returns a boolean.
//  select(item|items) - Selects the specified item. Pass an array of objects to select multiple
//    items (when multiple is enabled).
//  selectAll() - Selects all items.
//  selectNone() - Clears the selection.
//  invert() - Inverts the selection.
//  add(item) - Adds an item to the selection.
//  remove(item) - Removes an item from the selection.
//
export default {
  name: 'Selectable',
  props: {
    canSelect: {
      type: Function,
      default: () => true
    },
    items: {
      type: Array,
      default: () => []
    },
    keyProp: {
      type: String,
      default: 'id'
    },
    multiple: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      lastIndex: 0,
      selection: []
    };
  },
  watch: {
    // Make sure the selection only includes items that exist
    items() {
      const itemKeys = this.items.map(item => this.getKey(item));
      this.selection = this.selection.filter(key => {
        return itemKeys.includes(key);
      });
    },

    // Emit the input event when the selection changes (enables v-bind and @input)
    selection() {
      const selectedItems = this.items.filter(item => {
        const key = this.getKey(item);
        return this.selection.includes(key);
      });

      this.$emit('input', selectedItems);
    }
  },
  methods: {
    // Adds an item to the selection
    add(item) {
      const key = this.getKey(item);
      if (!this.isSelected(item) && this.canSelect(item)) {
        if (this.multiple) {
          this.selection.push(key);
        } else {
          this.selection = [key];
        }
      }
    },

    // Gets an item's unique key
    getKey(item) {
      return item[this.keyProp];
    },

    // Inverts the selection
    invert() {
      const selection = [];
      this.items.map(item => {
        const key = this.getKey(item);
        if (!this.selection.includes(key) && this.canSelect(item)) {
          selection.push(key);
        }
      });
      this.selection = selection;
    },

    // Tells if an item is selected
    isSelected(item) {
      const key = this.getKey(item);

      return this.selection.includes(key);
    },

    // Removes an item from the selection
    remove(item) {
      const key = this.getKey(item);
      this.selection = this.selection.filter(k => k !== key);
    },

    // Selects the specified item or a range of items
    select(item, event) {
      const key = this.getKey(item);
      const keys = this.items.map(item => this.getKey(item));
      const currentIndex = keys.indexOf(key);
      const isSelected = this.isSelected(item);
      const isMulti = event && (event.ctrlKey || event.metaKey);
      const isRange = event && event.shiftKey;

      if (this.multiple && isMulti) {
        // Multi select
        if (isSelected) {
          this.selection = this.selection.filter(k => k !== key);
        } else {
          if (this.canSelect(item)) {
            this.selection.push(key);
          }
        }
      } else if (this.multiple && isRange) {
        // Range select
        const min = Math.min(currentIndex, this.lastIndex);
        const max = Math.max(currentIndex, this.lastIndex);
        let selection = Array.from(this.selection);

        for (let i = min; i <= max; i++) {
          if (isSelected) {
            selection = selection.filter(k => k !== keys[i]);
          } else {
            if (selection.filter(k => k === keys[i]).length === 0) {
              selection.push(keys[i]);
            }
          }
        }

        // Remove items that can't be selected
        this.selection = selection.filter(key => {
          const item = this.items.filter(item => this.getKey(item) === key)[0];
          return this.canSelect(item);
        });
      } else {
        if (this.canSelect(item)) {
          this.selection = [key];
        } else {
          this.selection = [];
        }
      }

      this.lastIndex = currentIndex;
    },

    // Selects all items
    selectAll() {
      const items = this.items.filter(item => this.canSelect(item));
      const selection = items.map(item => this.getKey(item));
      this.selection = selection;
    },

    // Clears the selection
    selectNone() {
      this.selection = [];
    },

    // Selects one or more items
    setSelection(items) {
      items = Array.isArray(items) ? items : [items];
      const selection = [];
      items.map(item => {
        if (this.canSelect(item)) {
          selection.push(this.getKey(item));
        }
      });
      this.selection = selection;
    }
  }
};
</script>

<template>
  <div class="selectable">
    <slot />
  </div>
</template>
